import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
/* Helpers from atlas.core, types from atlas. Both are free of data, so the tenant's seed is
   the only market model in the bundle. */
import { siteTotal, placed } from '../data/atlas.core';
import type { Site, SegmentKey, Confidence, Pathway } from '../data/atlas';
/* The market model comes from the active tenant, not from Scira's seed. Importing the seed
   directly here is what put transplant centres and UK waiting lists into the pharmaceutical
   tenant's bundle: the flagship module was ignoring which tenant it was running as. */
import { TENANT } from '@tenant';

const ALL_SITES = TENANT.seed.site;
const COUNTRIES = TENANT.seed.country;
const ALL_PATHWAYS = TENANT.seed.pathway;
import { Boundary } from '../components/Boundary';
import { CaptureIntelligence } from '../components/Capture';
import { NewOpportunity, LogInteraction } from '../components/CreateForms';
import { Combo, type ComboOption } from '../components/Combo';
import { useList } from '../lib/useDb';
import { CriteriaPanel, ResultCount } from '../components/CriteriaPanel';
import { FilterActions } from '../components/FilterActions';
import { loadSearchableSites } from '../lib/siteSearch';
import { MarketBar, useMarket } from '../components/MarketBar';
import { siteFields, SITE_PRESETS, type SearchableSite } from '../lib/searchFields';
import { runSearch, type Criterion } from '../lib/search';
import { createAccountsFromSites } from '../lib/resultActions';
import './atlas.css';

/* =====================================================================================
   Atlas, the market model.

   Three views over one dataset. The Map is for spatial intuition, the Centres table is
   for work, and Reimbursement is for planning. All three respect the same filters, so
   moving between them never loses your place.
   ===================================================================================== */

type View = 'map' | 'centres' | 'reimbursement';
type SortKey = 'fit' | 'name' | 'volume_desc' | 'volume_asc' | 'tier' | 'country';

const ORGANS: SegmentKey[] = TENANT.vocabulary.segment?.keys ?? [];

/* The tenant names its own segments. Shared code asking a transplant lookup table what a
   'knee' is got `undefined` on the page, which is how the domain-bleed rule fails in
   practice rather than in principle. */
const segLabel = (k: SegmentKey): string =>
  TENANT.vocabulary.segment?.labels?.[k] ?? k;

/* Site kinds are the tenant's too. The centres table used to carry a hardcoded "H&I lab"
   column, which is a histocompatibility laboratory: a transplant concept that would appear as
   a column of dashes headed with somebody else's acronym in every other tenant. */
const KIND_COLUMNS = TENANT.vocabulary.siteKinds.filter((k) => k.column);
const MAP_KIND = TENANT.vocabulary.siteKinds.find((k) => k.column)?.key ?? null;

/* Two tiles and two columns exist only where the tenant has the data behind them. A waiting
   list is a fact about registries that publish one; a prevalent stock is a fact about a tenant
   that models one. A pharmaceutical tenant has neither, and "Active waiting list: —" on its
   market page is a transplant tile with the number filed off. Decided once, from the seed. */
/* One funnel per market for a multi-market tenant; the single `funnel` for everyone else. */
const FUNNELS = TENANT.vocabulary.funnels
  ?? (TENANT.vocabulary.funnel ? [TENANT.vocabulary.funnel] : []);
const HAS_WAITING = COUNTRIES.some((c) => Boolean((c as { waiting?: unknown }).waiting));
const HAS_PREVALENCE = Object.keys(TENANT.atlas.prevalenceMultipliers).length > 0
  || Boolean(TENANT.atlas.publishedPrevalence)
  || ALL_SITES.some((s) => Boolean(s.prevalentGrafts));

/* THE MARKET IS A LENS, AND THE ATLAS NOW LOOKS THROUGH IT.

   The selector used to tag uploaded documents and nothing else: Atlas showed every site and
   summed every volume regardless, so switching from IgA nephropathy to FSGS changed the label
   on the bar and not one number on the page. For a multi-market tenant that is worse than no
   selector, because it teaches the viewer that the control does nothing.

   With a market selected: sites that are not members of it drop out, and each remaining site
   keeps only the volumes counted in that market, so tiers, totals and the map all answer for
   that market. With none selected ("every market"), or for a tenant that has one market and no
   codes on its rows, everything is returned untouched: Scira's Atlas is byte-for-byte the same
   as before. Membership and codes come from the export; nothing here queries anything. */
function throughMarket(sites: Site[], code: string | null): Site[] {
  if (!code) return sites;
  const out: Site[] = [];
  for (const s of sites) {
    if (s.markets && !s.markets.includes(code)) continue;
    const volumes = s.volumes.filter((v) => !v.market || v.market === code);
    out.push(volumes.length === s.volumes.length ? s : { ...s, volumes });
  }
  return out;
}

/* National annual volume for one segment in one country.
   Prefers the generic `segments` map; falls back to the four fixed transplant columns so the
   Scira seed, which predates the map, keeps working untouched. */
const nationalFor = (c: Record<string, unknown>, key: SegmentKey): number => {
  const m = c.segments as Record<string, number | null> | undefined;
  if (m && key in m) return m[key] ?? 0;
  return (c as Record<string, number | null>)[key] ?? 0;
};

/* =====================================================================================
   Filter persistence.

   Opening a centre record navigates to /atlas/site/:id, which unmounts this whole view, so
   closing the record used to remount it with every filter back at zero: the cost of glancing
   at one record was losing the question you had spent a minute composing. The filters now
   live for the browser session, in sessionStorage, which is the pattern the demo banner and
   the showcase market picker already use: back and forward keep your place, a fresh visit
   starts clean, and nothing is written to any backend.

   Hydration validates against the CURRENT tenant seed. A remembered country no site in this
   bundle belongs to, or a remembered segment this tenant does not have, is dropped rather
   than applied, so a stale value can never filter the map down to nothing.
   ===================================================================================== */

const FILTER_STORE_KEY = 'forum.atlas.filters';
const SORT_KEYS: readonly SortKey[] = ['fit', 'name', 'volume_desc', 'volume_asc', 'tier', 'country'];
const VIEW_KEYS: readonly View[] = ['map', 'centres', 'reimbursement'];

interface StoredFilters {
  view: View; country: string; organ: SegmentKey | 'all'; q: string;
  sortBy: SortKey; criteria: Criterion[]; showFilters: boolean;
}

function loadStoredFilters(): Partial<StoredFilters> {
  try {
    const raw = sessionStorage.getItem(FILTER_STORE_KEY);
    if (!raw) return {};
    const p = JSON.parse(raw) as Partial<StoredFilters>;
    const out: Partial<StoredFilters> = {};
    if (p.view && VIEW_KEYS.includes(p.view)) out.view = p.view;
    if (p.country === 'all'
      || (typeof p.country === 'string' && COUNTRIES.some((c) => c.iso3 === p.country))) {
      out.country = p.country;
    }
    if (p.organ === 'all'
      || (typeof p.organ === 'string' && (ORGANS as string[]).includes(p.organ))) {
      out.organ = p.organ as SegmentKey | 'all';
    }
    if (typeof p.q === 'string') out.q = p.q;
    if (p.sortBy && SORT_KEYS.includes(p.sortBy)) out.sortBy = p.sortBy;
    if (Array.isArray(p.criteria)
      && p.criteria.every((c) => c && typeof c === 'object' && typeof c.field === 'string')) {
      out.criteria = p.criteria;
    }
    if (typeof p.showFilters === 'boolean') out.showFilters = p.showFilters;
    return out;
  } catch {
    return {};   // Malformed or unavailable storage reads as "nothing remembered".
  }
}

function Conf({ c }: { c: Confidence }) {
  const label = { verified: 'Verified', derived: 'Derived', estimated: 'Estimated', unverified: 'Unverified' }[c];
  return <span className={`conf ${c}`} title={`Data confidence: ${label}`}>{label}</span>;
}

function TierPill({ tier }: { tier: 1 | 2 | 3 }) {
  const cls = tier === 1 ? 'accent' : tier === 2 ? 'info' : 'mute';
  return <span className={`pill ${cls}`}>Tier {tier}</span>;
}

const fmt = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('en-GB'));

/* --------------------------------------------------------------------------------- */

/*
 * The line under the waiting list figure.
 *
 * It has to carry three things a reader needs before quoting the number: how much of the
 * scope is actually covered, what the registries counted, and how stale the oldest input is.
 * A waiting list published annually is a stock at a date, so "as at" is not decoration.
 */
function waitingFoot(t: {
  waitingCountries: number; waitingMissing: number; waitingScope: number;
  waitingBasis: string | null; waitingAsAt: string | null; waitingPerOrgan?: boolean;
  waitingSource: { name: string; url?: string } | null;
}): string {
  if (!t.waitingCountries) return 'No published national figure';

  const parts: string[] = [];
  parts.push(t.waitingMissing === 0
    ? (t.waitingScope === 1 ? 'National registry figure' : `All ${t.waitingScope} countries reporting`)
    : `${t.waitingCountries} of ${t.waitingScope} countries reporting`);

  // Say plainly when the number is not what its label implies.
  if (t.waitingBasis === 'mixed') parts.push('mixed basis: some lists include suspended patients');
  else if (t.waitingBasis === 'total') parts.push('includes suspended and inactive');
  else if (t.waitingBasis === 'unclear') parts.push('basis not stated by the registry');

  if (t.waitingAsAt) {
    const d = new Date(t.waitingAsAt);
    if (!Number.isNaN(d.getTime())) {
      parts.push(`at ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`);
    }
  }
  if (t.waitingPerOrgan) parts.push('some registries list a patient under each organ needed, so organs are not additive');
  if (t.waitingCountries === 1 && t.waitingSource) parts.push(t.waitingSource.name);
  return parts.join(' \u00b7 ');
}

export function AtlasView() {
  const { markets, marketId } = useMarket();
  const marketCode = marketId ? (markets.find((m) => m.id === marketId)?.code ?? null) : null;
  const SITES = useMemo(() => throughMarket(ALL_SITES, marketCode), [marketCode]);
  /* Read once on mount. Reading sessionStorage inside every initializer would parse the same
     JSON seven times for no reason. */
  const [remembered] = useState<Partial<StoredFilters>>(loadStoredFilters);
  const [view, setView] = useState<View>(remembered.view ?? 'map');
  const [organ, setOrgan] = useState<SegmentKey | 'all'>(remembered.organ ?? 'all');
  const [country, setCountry] = useState<string>(remembered.country ?? 'all');
  const [selected, setSelected] = useState<Site | null>(null);
  const [q, setQ] = useState(remembered.q ?? '');
  /* The criteria live HERE, not inside the builder, which is the whole change. One set of
     filters drives the map, the table and the export, so narrowing to mid-size units with a
     colocated laboratory redraws the map to those units instead of leaving it showing all
     1,433 while a separate tab quietly held the answer. */
  const [criteria, setCriteria] = useState<Criterion[]>(remembered.criteria ?? []);
  const [showFilters, setShowFilters] = useState(remembered.showFilters ?? false);
  const [sortBy, setSortBy] = useState<SortKey>(remembered.sortBy ?? 'fit');

  /* Every change is written back, so opening a record and returning, or leaving for another
     module and coming back, lands on the same question. Storage full or forbidden is not an
     error worth surfacing: the page simply behaves as before. */
  useEffect(() => {
    try {
      sessionStorage.setItem(FILTER_STORE_KEY,
        JSON.stringify({ view, country, organ, q, sortBy, criteria, showFilters }));
    } catch { /* private browsing or quota; persistence is best effort */ }
  }, [view, country, organ, q, sortBy, criteria, showFilters]);

  const filtersActive = country !== 'all' || organ !== 'all' || q !== ''
    || criteria.length > 0 || sortBy !== 'fit';
  /* One explicit way back to "everything". Before this, clearing four filters meant finding
     four controls, and the only full reset was reloading the page. */
  function resetFilters() {
    setCountry('all'); setOrgan('all'); setQ(''); setCriteria([]); setSortBy('fit');
  }
  /* Enriched rows, loaded once for the whole page. The seed alone cannot answer "sends work
     out" or "has a long-read sequencer"; those live in Postgres. Until they arrive the page
     works on the seed, so the map never waits for a network call. */
  const [enriched, setEnriched] = useState<SearchableSite[] | null>(null);
  useEffect(() => {
    loadSearchableSites().then(setEnriched).catch(() => setEnriched(null));
  }, []);

  /* Sorted by name using a locale comparator, so accented names file where a reader expects
     rather than after Z. Only countries that actually have a mapped site are offered: the
     others would filter the map to nothing and look like a fault. */
  const countryOptions: ComboOption[] = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of SITES) counts.set(s.iso3, (counts.get(s.iso3) ?? 0) + 1);
    const list = COUNTRIES
      .filter((c) => counts.has(c.iso3))
      .map((c) => ({
        value: c.iso3,
        label: c.name,
        hint: `${counts.get(c.iso3)} ${counts.get(c.iso3) === 1
          ? TENANT.vocabulary.site.toLowerCase()
          : TENANT.vocabulary.sitePlural.toLowerCase()}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'en'));
    return [{ value: 'all', label: 'All countries', hint: `${SITES.length}` }, ...list];
  }, [SITES]);

  /* The pickers first, because they are the fast common case and people expect them to keep
     working exactly as they did. */
  const picked = useMemo(() => {
    return SITES.filter((s) => {
      if (country !== 'all' && s.iso3 !== country) return false;
      if (organ !== 'all' && !s.volumes.some((v) => v.organ === organ)) return false;
      if (q && !(`${s.name} ${s.city} ${s.region}`.toLowerCase().includes(q.toLowerCase()))) return false;
      return true;
    }).sort((a, b) => siteTotal(b, organ === 'all' ? undefined : organ) - siteTotal(a, organ === 'all' ? undefined : organ));
  }, [SITES, organ, country, q]);

  const searchFields = useMemo(
    () => siteFields((enriched ?? (picked as unknown as SearchableSite[]))), [enriched, picked]);

  /* Then the criteria, narrowing what the pickers left. Both are filters and they compose, so
     the count in the toolbar is always the number of things actually on screen.

     No limit is applied. A limit belongs on a ranked ANSWER, not on a map: capping the map at
     100 would silently hide centres inside the area being looked at. */
  const found = useMemo(() => {
    if (!criteria.length) return null;
    const byId = new Map((enriched ?? []).map((e) => [e.id, e]));
    const rows = picked.map((s) => (byId.get(s.id) ?? s) as unknown as SearchableSite);
    return runSearch(rows, searchFields, criteria, { fallbackSort: { field: 'total', direction: 'desc' } });
  }, [criteria, picked, enriched, searchFields]);

  /* If the criteria are about ONE segment, the results list shows THAT number. Filtering on
     "Kidney / year between 100 and 200" while the column showed 691 total transplants made a
     correct row look like a broken one, and made a genuinely broken one much harder to see. */
  const criterionSegment = useMemo(() => {
    const k = criteria.map((c) => c.field).find((f) => f.startsWith('vol_'));
    return k ? (k.slice(4) as SegmentKey) : null;
  }, [criteria]);

  /* What every view below renders.

     ORDER IS EXPLICIT NOW. It used to be score first and then TOTAL volume as a tiebreak, and
     with only MUST criteria every row scores the same, so the whole list fell through to total.
     Filter to heart programmes and a centre doing 20 hearts out of 600 transplants sat above one
     doing 100 hearts, which reads as unsorted. Volume here means the volume you filtered on. */
  const sites = useMemo(() => {
    const base = found ? found.hits.map((h) => h.row as unknown as Site) : picked;
    const seg = criterionSegment ?? (organ === 'all' ? undefined : organ);
    const vol = (s: Site) => siteTotal(s, seg);
    const rows = [...base];
    switch (sortBy) {
      case 'name':
        return rows.sort((a, b) => (a.short || a.name).localeCompare(b.short || b.name, 'en'));
      case 'volume_desc': return rows.sort((a, b) => vol(b) - vol(a));
      case 'volume_asc':  return rows.sort((a, b) => vol(a) - vol(b));
      case 'tier':        return rows.sort((a, b) => a.tier - b.tier || vol(b) - vol(a));
      case 'country':
        return rows.sort((a, b) => a.iso3.localeCompare(b.iso3) || vol(b) - vol(a));
      case 'fit':
      default:
        /* Best fit, which without a PREFER criterion means the volume actually asked about
           rather than the all-organ total. */
        return found && criteria.some((c) => c.mode === 'prefer')
          ? rows
          : rows.sort((a, b) => vol(b) - vol(a));
    }
  }, [found, picked, sortBy, criterionSegment, organ, criteria]);

  const scoreById = useMemo(() => {
    if (!found || !criteria.some((c) => c.mode === 'prefer')) return null;
    return new Map(found.hits.map((h) => [(h.row as unknown as Site).id, h.score]));
  }, [found, criteria]);

  /* THE MARKET IS THE COUNTRY, NOT THE CENTRES WE HAPPEN TO HAVE MAPPED.
     These tiles used to sum the mapped sites and label the result as though it were the
     national figure. For Brazil that showed 3,449 against a verified national 9,793, because
     only 16 of 162 centres are named in the registry — understating the largest market in
     the Americas by two thirds. The national volumes are verified against ABTO, CIHI,
     CENATRA and the rest, so they are the headline; what we have mapped is COVERAGE, and it
     is shown as such underneath. */
  /* Names as the basemap spells them. A few differ from our registry names, so the map is
     matched loosely and anything that does not match simply stays untinted rather than
     claiming coverage we do not have. */
  const mappedCountryNames = useMemo(() => {
    const n = new Set<string>();
    for (const c of COUNTRIES) {
      n.add(c.name);
      if (c.name === 'United States') n.add('United States of America');
      if (c.name === 'United Kingdom') n.add('United Kingdom');
      if (c.name === 'Czechia') n.add('Czech Republic');
      if (c.name === 'Dominican Republic') n.add('Dominican Rep.');
    }
    return n;
  }, []);

  const totals = useMemo(() => {
    const centres = sites.length;

    // What our mapped centres account for. Real, but a subset.
    const mapped = sites.reduce((a, s) => a + siteTotal(s, organ === 'all' ? undefined : organ), 0);

    const inScope = country === 'all'
      ? COUNTRIES
      : COUNTRIES.filter((c) => c.iso3 === country);

    /* Which segments have a published NATIONAL total, as opposed to only the sum of the
       centres we happen to have mapped. The tenant declares this: it used to be the literal
       ['kidney','liver','heart','lung'], which is transplantation's shape imposed on every
       customer. Where a segment is not on the list there is no national figure, so the mapped
       sum is shown and labelled as such rather than passed off as national. */
    const NATIONAL = TENANT.atlas.nationalSegments;
    const nationalAvailable = organ === 'all' || NATIONAL.includes(organ);

    const national = nationalAvailable
      ? inScope.reduce((a, c) => a + (organ === 'all'
          ? NATIONAL.reduce((n, k) => n + nationalFor(c as never, k), 0)
          : nationalFor(c as never, organ)), 0)
      : 0;

    const volume = nationalAvailable && national > 0 ? national : mapped;
    const isNational = nationalAvailable && national > 0;

    /* The prevalent population derived from the NATIONAL incident volume using the tenant's own
       multipliers, so the addressable base matches the market rather than the sample. For Scira
       that is graft-years; for a chronic disease it is mean duration; for an implant market it
       is how long a device stays in service. Same arithmetic, tenant's meaning. */
    /* A PUBLISHED COUNT BEATS THE ARITHMETIC, and replaces it rather than joining it.

       Four registries publish how many people are living with a functioning kidney graft. Where
       one does, that number is used and the multiplier is not applied to that country at all;
       mixing a census with a model produces a figure that is neither. The multiplier remains
       for everyone else, and the caveats say which countries are which. */
    const published = TENANT.atlas.publishedPrevalence ?? {};
    const prevalent = isNational
      ? Math.round(inScope.reduce((a, c) => {
          const m = TENANT.atlas.prevalenceMultipliers;
          const pub = published[c.iso3];
          if (organ !== 'all') {
            if (pub && pub.organ === organ) return a + pub.value;
            return a + nationalFor(c as never, organ) * (m[organ] ?? 0);
          }
          return a + NATIONAL.reduce((n, k) => {
            if (pub && pub.organ === k) return n + pub.value;
            return n + nationalFor(c as never, k) * (m[k] ?? 0);
          }, 0);
        }, 0))
      : sites.reduce((a, s) => {
          if (organ === 'all') return a + (s.prevalentGrafts?.value ?? 0);
          const split = s.prevalentByOrgan ?? {};
          /* Some registries report two segments as one combined category, so selecting either
             part must pull its share of the combined row or the figure silently under-reads.
             Which categories those are is the tenant's business: NHSBT reports heart and lung
             together, and an implant registry has no equivalent. */
          const extra = (TENANT.atlas.combinedSplits?.[organ] ?? [])
            .reduce((n, c) => n + (split[c.from] ?? 0) * c.share, 0);
          return a + (split[organ] ?? 0) + extra;
        }, 0);

    // How much of the national picture our named centres actually cover.
    const coveragePct = isNational && volume > 0 ? Math.round((mapped / volume) * 100) : null;
    const knownCentres = inScope.reduce((a, c) =>
      a + ((c as never as Record<string, number | null>).centreCount ?? 0), 0);

    /* The national waiting list.
     *
     * This used to sum whatever waiting figures our mapped centres happened to carry and label
     * the result a national total. With eleven centres reporting out of eight hundred it read
     * 3,326, which was not a national waiting list, not an estimate of one, and not comparable
     * to anything else on this screen.
     *
     * It is now summed from published national registry figures only, and only across
     * countries that report on the SAME basis, because an active list added to a total
     * including suspended patients is a number with no meaning. Where bases differ the
     * interface says so rather than quietly producing one figure. */
    /* Which segments the tenant has published national waiting figures for. Elective surgical
       waiting lists are as real as transplant ones and often more politically visible, so this
       generalises rather than disappearing outside transplantation. */
    const waitingOrgans: readonly string[] = organ === 'all' ? NATIONAL : [organ];

    let waiting = 0;
    let waitingCountries = 0;
    const bases = new Set<string>();
    let oldestAsAt: string | null = null;
    let perOrganCounting = false;
    let soleSource: { name: string; url?: string } | null = null;

    for (const c of inScope) {
      const w = c.waiting;
      if (!w) continue;
      const wm = (w as never as { segments?: Record<string, number | null> }).segments;
      const n = waitingOrgans.reduce(
        (a, o) => a + ((wm && o in wm ? wm[o] : (w as never as Record<string, number | null>)[o]) ?? 0), 0);
      if (!n) continue;                       // publishes a list, but not for this organ
      waiting += n;
      waitingCountries += 1;
      bases.add(w.basis);
      if (!oldestAsAt || w.asAt < oldestAsAt) oldestAsAt = w.asAt;
      // Eurotransplant lists a candidate under every organ they need, so a simultaneous
      // kidney-pancreas patient is already inside both rows. Adding organs together for these
      // countries counts them twice, and the tile has to say so rather than inflate.
      if (waitingOrgans.length > 1 && TENANT.atlas.countsPerOrganNeeded.has(c.iso3)) perOrganCounting = true;
      soleSource = waitingCountries === 1 ? { name: w.source, url: w.sourceUrl } : soleSource;
    }

    // How many countries in scope publish nothing we could verify. Shown, not hidden: a
    // reader comparing two regions needs to know one of them is half dark.
    const waitingMissing = inScope.length - waitingCountries;

    return { centres, volume, mapped, prevalent, isNational, coveragePct, knownCentres,
             waiting, waitingCountries, waitingMissing, waitingScope: inScope.length,
             waitingBasis: bases.size === 1 ? [...bases][0] : bases.size > 1 ? 'mixed' : null,
             waitingAsAt: oldestAsAt, waitingPerOrgan: perOrganCounting,
             waitingSource: waitingCountries === 1 ? soleSource : null };
  }, [sites, organ, country]);

  return (
    <div className="page atlas">
      <div className="page-head">
        <h1 className="page-title">{TENANT.vocabulary.atlas}</h1>
        <p className="page-sub">{TENANT.vocabulary.atlasSubtitle}</p>
      </div>

      {/* Renders nothing for a tenant with a single market, which is most of them. */}
      <MarketBar />

      <div className="toolbar">
        <div className="seg">
          {(['map', 'centres', 'reimbursement'] as View[]).map((v) => (
            <button key={v} className={view === v ? 'on' : ''} onClick={() => setView(v)}>
              {v === 'map' ? 'Map'
                : v === 'centres' ? TENANT.vocabulary.sitePlural
                : 'Reimbursement'}
            </button>
          ))}
        </div>

        {/* Typed, not scrolled. With 78 countries a native select means hunting, and its own
            keyboard matching only works from the first letter and resets between keystrokes.
            Sorted by name rather than by whatever order the seed happened to load in. */}
        <Combo
          value={country}
          onChange={setCountry}
          options={countryOptions}
          ariaLabel="Filter by country"
          placeholder="All countries"
          width={210}
        />

        {/* Removed, not renamed, where the tenant has no segmentation dimension. There are no
            organs in a single-indication market and an empty filter is worse than none. */}
        {TENANT.vocabulary.segment && (
          <select className="input sel" value={organ}
                  onChange={(e) => setOrgan(e.target.value as SegmentKey | 'all')}>
            <option value="all">{TENANT.vocabulary.segment.label}</option>
            {ORGANS.map((o) => <option key={o} value={o}>{segLabel(o)}</option>)}
          </select>
        )}

        <input
          className="input search"
          placeholder={`Filter ${TENANT.vocabulary.sitePlural.toLowerCase()}…`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        {/* In the toolbar rather than inside the filter panel, because the order of the list is
            worth changing whether or not you are editing criteria, and the panel is collapsed
            most of the time. */}
        <select className="input sel" value={sortBy} aria-label="Sort by"
                onChange={(e) => setSortBy(e.target.value as SortKey)}>
          <option value="fit">
            {criteria.some((c) => c.mode === 'prefer') ? 'Sort: best fit' : 'Sort: volume, high to low'}
          </option>
          <option value="volume_desc">Sort: volume, high to low</option>
          <option value="volume_asc">Sort: volume, low to high</option>
          <option value="name">Sort: name, A to Z</option>
          <option value="tier">Sort: tier</option>
          <option value="country">Sort: country</option>
        </select>

        {/* The count of criteria is on the button, because filters that are collapsed and active
            are how somebody concludes the data is missing. */}
        <button className={`btn${criteria.length ? ' primary' : ''}`}
                onClick={() => setShowFilters((v) => !v)}>
          {showFilters ? 'Hide filters' : 'Filters'}
          {criteria.length > 0 && ` (${criteria.length})`}
        </button>

        {/* Disabled rather than hidden when there is nothing to clear, so the way out is
            always visible before it is needed. */}
        <button className="btn" onClick={resetFilters} disabled={!filtersActive}
                title="Clear the country, segment, text and criteria filters">
          Reset filters
        </button>
      </div>

      {/* A country whose registry reports nationally is not a country we failed to load. Said
          here, next to the list of blank centres, because that is where the wrong conclusion
          gets drawn. */}
      {country !== 'all' && (() => {
        const c = COUNTRIES.find((x) => x.iso3 === country);
        if (c?.centreData !== 'national_only') return null;
        const blank = sites.filter((s) => siteTotal(s) === 0).length;
        return (
          <p className="block-note atlas-availability">
            <strong>{c.name} publishes activity nationally, not per centre.</strong>{' '}
            {blank} of the {sites.length} {TENANT.vocabulary.sitePlural.toLowerCase()} here carry
            no volume for that reason rather than because the load is unfinished. The national
            figure is the verified one.
          </p>
        );
      })()}

      {showFilters && (
        <section className="card atlas-filters">
          <p className="block-lead">
            Every criterion is either <strong>must</strong>, which excludes, or{' '}
            <strong>prefer</strong>, which ranks without excluding. That distinction is what lets
            you ask for mid-size units in one country with a colocated laboratory and still see
            the near misses. These apply to the map and the table alike.
          </p>
          <CriteriaPanel<SearchableSite>
            fields={searchFields}
            criteria={criteria}
            setCriteria={setCriteria}
            presets={SITE_PRESETS as never}
          />
          {found && (
            <ResultCount shown={sites.length} total={found.total} criteria={criteria}
                         excludedBy={found.excludedBy} fields={searchFields} />
          )}

          {/* THE ANSWER, WHERE THE QUESTION WAS ASKED. The results used to live only on the
              table tab, so filtering while looking at the map gave a count, an export button and
              nothing to read. You had to know to change tab to see what you had just asked for.

              Ranked, and every row opens the record. Capped at 200 on screen because this is a
              panel and not the table view; the export and the count are always the full set, and
              the cap says so rather than trailing off silently. */}
          {found && sites.length > 0 && (
            <div className="atlas-hits">
              <table className="data">
                <thead>
                  <tr>
                    <th className="num">#</th>
                    {scoreById && <th className="num">Fit</th>}
                    <th>{TENANT.vocabulary.site}</th>
                    <th>Location</th>
                    <th className="num">
                      {criterionSegment ? `${segLabel(criterionSegment)} / yr`
                        : organ === 'all' ? TENANT.vocabulary.volumeLabel
                        : `${segLabel(organ)} / yr`}
                    </th>
                    <th>Why</th>
                  </tr>
                </thead>
                <tbody>
                  {found.hits.slice(0, 200).map((h, i) => {
                    const s = h.row as unknown as Site;
                    return (
                      <tr key={s.id} className="sb-hit"
                          onClick={() => setSelected(s)} style={{ cursor: 'pointer' }}>
                        <td className="num muted">{i + 1}</td>
                        {scoreById && (
                          <td className="num">
                            <span className="sb-score"
                                  style={{ opacity: 0.35 + (h.score / 100) * 0.65 }}>
                              {h.score.toFixed(0)}
                            </span>
                          </td>
                        )}
                        <td>
                          <div className="cell-title">{s.short || s.name}</div>
                          <div className="cell-sub">{s.name}</div>
                        </td>
                        <td>
                          <div className="cell-title">{s.city}</div>
                          <div className="cell-sub">{s.region || COUNTRIES.find((c) => c.iso3 === s.iso3)?.name || s.iso3}</div>
                        </td>
                        <td className="num">
                          {siteTotal(s, criterionSegment ?? (organ === 'all' ? undefined : organ))
                            .toLocaleString('en-GB')}
                        </td>
                        <td className="cell-sub">
                          {h.reasons.filter((r) => r.met).map((r) => r.label).join(', ')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {sites.length > 200 && (
                <p className="atlas-hits-more">
                  Showing the first 200 of {sites.length.toLocaleString('en-GB')}. The map, the
                  export and the actions below all use the full set.
                </p>
              )}
            </div>
          )}
          <FilterActions
            module="atlas.sites"
            criteria={criteria}
            setCriteria={setCriteria}
            resultCount={sites.length}
            exportName={`atlas-${new Date().toISOString().slice(0, 10)}`}
            exportColumns={[
              { key: 'name', label: TENANT.vocabulary.site },
              { key: 'city', label: 'City' },
              { key: 'region', label: 'Region' },
              { key: 'iso3', label: 'Country' },
              { key: 'tier', label: 'Tier' },
              { key: 'total', label: TENANT.vocabulary.volumeLabel },
              { key: 'score', label: 'Fit' },
            ]}
            exportRows={() => sites.map((s) => ({
              name: s.name, city: s.city ?? '', region: s.region ?? '', iso3: s.iso3,
              tier: s.tier, total: siteTotal(s, organ === 'all' ? undefined : organ),
              score: scoreById?.get(s.id) ?? '',
            }))}
            actLabel={`Create ${sites.length.toLocaleString('en-GB')} CRM accounts`}
            onAct={async () => {
              const r = await createAccountsFromSites(sites.map((s) => ({
                siteId: s.id, name: s.name, country: s.iso3, city: s.city,
                region: s.region, kinds: s.kinds, tier: s.tier,
              })));
              return `${r.created} created, ${r.existing} already had an account.`;
            }}
          />
        </section>
      )}

      {/* The narrowing from headline population to sellable population. Declared by the
          tenant because not every market has one, and shown near the tiles because the top
          tile is the number people quote and this is the number that matters. */}
      {country === 'all' && FUNNELS
        .filter((f) => !marketCode || !f.market || f.market === marketCode)
        .map((funnel) => (
        <section className="funnel card" key={funnel.title}>
          <h2 className="funnel-h">{funnel.title}</h2>
          <p className="funnel-note">{funnel.note}</p>
          <ol className="funnel-steps">
            {funnel.steps.map((st, i, arr) => {
              const top = arr[0].value ?? 0;
              const pct = st.value && top ? Math.max(2, Math.round((st.value / top) * 100)) : 0;
              return (
                <li key={st.label}>
                  <div className="funnel-bar" style={{ width: `${pct}%` }} aria-hidden="true" />
                  <div className="funnel-body">
                    <div className="funnel-top">
                      <strong>{st.label}</strong>
                      <span className="funnel-val">{st.value != null ? fmt(st.value) : '—'}</span>
                    </div>
                    <div className="funnel-basis">{st.basis}</div>
                    {/* The source is on the row, not in a footnote. Every step here is an
                        assumption somebody will want to argue with. */}
                    <div className="funnel-src">{st.source}</div>
                    {i > 0 && arr[i - 1].value && st.value && (
                      /* RETAINED, not dropped. This previously computed 1 - ratio and labelled
                         it "of the row above", so 218,000 of 273,041 read as 20% when it is
                         80%. Both numbers are legitimate to show; labelling one as the other
                         is not. Retained is what the row is actually reporting. */
                      <div className="funnel-drop">
                        {Math.round((st.value / (arr[i - 1].value as number)) * 100)}% of the row
                        above{' '}
                        <span className="funnel-lost">
                          ({fmt((arr[i - 1].value as number) - st.value)} fall out)
                        </span>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      ))}

      {/* Stated on the page rather than left as a silent gap. A world map with no China on
          it invites the reader to assume it was counted; saying so is the honest alternative.
          Tenant text, and absent entirely where a tenant has nothing to caveat. */}
      {TENANT.vocabulary.notMapped && (
        <p className="atlas-note not-mapped">
          <strong>Not mapped:</strong> {TENANT.vocabulary.notMapped}
        </p>
      )}

      <div className="metrics">
        <Metric
          label={`${TENANT.vocabulary.sitePlural} mapped`}
          value={fmt(totals.centres)}
          foot={totals.knownCentres > totals.centres
            ? `of ${fmt(totals.knownCentres)} known to exist`
            : undefined}
        />
        <Metric
          label={organ === 'all' ? TENANT.vocabulary.volumeLabel : `${segLabel(organ)} / year`}
          value={fmt(totals.volume)}
          foot={totals.isNational
            ? (totals.coveragePct != null
                ? `National. Mapped ${TENANT.vocabulary.sitePlural.toLowerCase()} cover ${totals.coveragePct}%`
                : 'National registry figure')
            : `Mapped ${TENANT.vocabulary.sitePlural.toLowerCase()} only; no national figure published`}
          tone={totals.isNational ? 'verified' : 'estimated'}
        />
        {HAS_PREVALENCE && <Metric
          label={organ === 'all'
            ? TENANT.vocabulary.population
            // Per-segment variant. Built from the tenant's own noun so a segmented tenant
            // reads "Prevalent grafts, kidney" and never borrows another domain's word.
            : `${TENANT.vocabulary.population}, ${segLabel(organ).toLowerCase()}`}
          value={fmt(totals.prevalent)}
          foot={TENANT.vocabulary.populationFoot}
          tone="derived"
        />}
        {HAS_WAITING && <Metric
          label={totals.waitingBasis === 'total' ? 'Waiting list (incl. suspended)' : 'Active waiting list'}
          value={totals.waitingCountries ? fmt(totals.waiting) : '—'}
          foot={waitingFoot(totals)}
          // Verified only when every country in scope publishes on the same basis. A mixed
          // basis is a real qualification on the number, so it reads as derived, not verified.
          tone={!totals.waitingCountries ? 'unverified'
            : totals.waitingBasis === 'active' && !totals.waitingMissing ? 'verified'
            : 'derived'}
        />}
      </div>

      {/* The caveats travel with the figures. Collapsed by default because they are long,
          present at all because a waiting list quoted without its basis is a wrong number:
          registries count different populations and several of these are totals, not active
          lists. Anyone about to put one of these in a board paper needs this. */}
      {totals.waitingCountries > 0 && TENANT.atlas.caveats.length > 0 && (
        <details className="wl-caveats">
          <summary>
            How to read the waiting list figures
            <span className="wl-count">{TENANT.atlas.caveats.length} qualifications</span>
          </summary>
          <ul>
            {TENANT.atlas.caveats.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </details>
      )}

      {view === 'map' && <Boundary what="The map"><MapView sites={sites} organ={organ} selected={selected}
                 mappedNames={mappedCountryNames}
                 onPickCountry={(n) => {
                   const c = COUNTRIES.find((x) => x.name === n
                     || n.startsWith(x.name) || x.name.startsWith(n));
                   if (c) setCountry(c.iso3);
                 }} /></Boundary>}
      {view === 'centres' && <CentresTable sites={sites} organ={organ} onSelect={setSelected}
                 scoreById={scoreById} />}

      {view === 'reimbursement' && <ReimbursementBoard country={country} market={marketCode} />}

      {selected && <SiteDrawer site={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function Metric({ label, value, foot, tone }: { label: string; value: string; foot?: string; tone?: Confidence }) {
  return (
    <div className="metric card">
      <div className="label">{label}</div>
      <div className="metric-value">{value}</div>
      {foot && (
        <div className="metric-foot">
          {tone ? <Conf c={tone} /> : <span>{foot}</span>}
          {tone && <span className="metric-foot-note">{foot}</span>}
        </div>
      )}
    </div>
  );
}

/* =====================================================================================
   Map. An equirectangular projection auto-fitted to the data bounds, so the view frames
   whatever is filtered rather than always showing an ocean. Dot area is proportional to
   volume (area, not radius, because radius exaggerates).
   ===================================================================================== */

interface Country { n: string; r: number[][][] }

function MapView({
  sites, organ, selected, mappedNames, onPickCountry,
}: {
  sites: Site[]; organ: SegmentKey | 'all'; selected: Site | null;
  mappedNames: Set<string>; onPickCountry: (name: string) => void;
}) {
  const [world, setWorld] = useState<Country[]>([]);
  useEffect(() => {
    // Coastlines are context, not content: the map is useful the moment the dots appear and
    // simply becomes legible when this arrives a fraction of a second later.
    import('../data/world.json').then((m) => setWorld(m.default as Country[])).catch(() => {});
  }, []);
  const W = 1000, H = 560, PAD = 60;
  const navigate = useNavigate();

  /* Zoom and pan sit on a transform over the projected layer rather than on the projection
     itself, so the auto-fit still frames whatever is filtered and zoom composes on top of
     it. Resetting the filter therefore also resets the frame, which is what people expect. */
  const [z, setZ] = useState({ k: 1, x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const dragMoved = useRef(false);
  const frame = useRef<number | null>(null);
  /* Two nearby hospitals in one city are a few hundredths of a degree apart, which at 16x
     was still under a dot's width on a country-wide frame. 64x resolves centres about a
     kilometre apart; everything drawn inside the transform is counter-scaled, so nothing
     thickens or balloons on the way in. */
  const clamp = (k: number) => Math.min(64, Math.max(1, k));

  /* Touch: active pointers for the pinch gesture. touch-action is none, so the browser hands
     both fingers to us and the page underneath never scrolls or double-tap-zooms instead. */
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ d: number } | null>(null);

  /* The tooltip. A real element rather than an SVG <title>, because the native tooltip
     appears after a second on hover and never on touch, and identifying a dot without
     opening it is the whole point of hovering. Pinned means it was opened by a tap and
     stays until the next tap; hover tips clear themselves on leave. */
  const [tip, setTip] = useState<Site | null>(null);
  const tipPinned = useRef(false);
  const downOnNode = useRef<string | null>(null);
  const lastPointerType = useRef('mouse');
  useEffect(() => { setTip(null); tipPinned.current = false; }, [sites]);

  /* React registers wheel listeners passively, so the zoom handler cannot stop the shell's
     content pane scrolling behind the map at the same time. A native non-passive listener
     exists solely to preventDefault; the zoom itself stays in onWheel below. */
  const svgRef = useRef<SVGSVGElement | null>(null);
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const stop = (e: WheelEvent) => e.preventDefault();
    el.addEventListener('wheel', stop, { passive: false });
    return () => el.removeEventListener('wheel', stop);
  }, []);

  /* Client coordinates to viewBox coordinates, shared by wheel, pinch and double click. */
  function toView(pt: { clientX: number; clientY: number }, el: SVGSVGElement) {
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return { x: W / 2, y: H / 2 };
    return { x: ((pt.clientX - r.left) / r.width) * W, y: ((pt.clientY - r.top) / r.height) * H };
  }

  function zoomAt(factor: number, cx = W / 2, cy = H / 2) {
    setZ((p) => {
      const k = clamp(p.k * factor);
      if (k === p.k) return p;
      // Keep the point under the cursor fixed while scaling.
      return { k, x: cx - ((cx - p.x) * k) / p.k, y: cy - ((cy - p.y) * k) / p.k };
    });
  }

  function onWheel(e: React.WheelEvent<SVGSVGElement>) {
    const r = e.currentTarget.getBoundingClientRect();
    if (!r.width || !r.height) return;
    /* Read every value from the event NOW. The callback below runs a frame later, by which
       time the synthetic event may have been recycled and currentTarget is certainly gone. */
    const cx = ((e.clientX - r.left) / r.width) * W;
    const cy = ((e.clientY - r.top) / r.height) * H;
    // A trackpad emits wheel events far faster than React can re-render 1,300 nodes.
    // Coalescing to one update per frame is the difference between smooth and clunky.
    // Magnitude-aware: a gentle two-finger scroll should not jump a whole zoom step.
    const step = Math.min(1.35, 1 + Math.abs(e.deltaY) / 400);
    const up = e.deltaY < 0;
    if (frame.current != null) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      zoomAt(up ? step : 1 / step, cx, cy);
    });
  }

  /* SITES THAT SHARE A COORDINATE, FANNED OUT.

     891 of the 1,334 mapped sites sit on a coordinate another site also occupies, across 251
     locations. Three institutions share Addenbrooke's point in Cambridge and thirteen Italian
     centres share the centroid of Italy. Because the coordinates are identical, no amount of
     zooming separates them: they render as one dot, the topmost swallows every click, and the
     record that opens is whichever happened to draw last. That is why a laboratory listed as
     tier 2 in the table opened a tier 1 hospital in the drawer. It was never a tiering fault.

     The offset is a golden-angle spiral in PROJECTED units, so it is negligible when zoomed out
     and grows with the zoom transform: co-located sites converge to a single point at world
     view and fan into a ring you can pick from as you go in, which is the behaviour that makes
     the map usable rather than decorative. Deterministic, so a site does not move between
     renders. */
  const { spread, clusters } = useMemo(() => {
    const groups = new Map<string, { ids: string[]; lat: number; lon: number }>();
    for (const s of sites) {
      if (!placed(s)) continue;
      const k = `${s.lat.toFixed(4)},${s.lon.toFixed(4)}`;
      const g = groups.get(k);
      if (g) g.ids.push(s.id); else groups.set(k, { ids: [s.id], lat: s.lat, lon: s.lon });
    }
    const off = new Map<string, { dx: number; dy: number }>();
    /* Shared coordinates, remembered so the map can say "3" over a stack that has not fanned
       out yet. Without the count, three institutions on one point read as one institution,
       and nothing on screen suggests zooming would reveal more. */
    const shared: { lat: number; lon: number; n: number }[] = [];
    for (const g of groups.values()) {
      if (g.ids.length === 1) continue;
      shared.push({ lat: g.lat, lon: g.lon, n: g.ids.length });
      // Sorted so the arrangement does not depend on filter order.
      [...g.ids].sort().forEach((id, i) => {
        const angle = i * 2.399963;                 // golden angle, evenly fills a disc
        const rad = 2.6 * Math.sqrt(i + 0.6);
        off.set(id, { dx: Math.cos(angle) * rad, dy: Math.sin(angle) * rad });
      });
    }
    return { spread: off, clusters: shared };
  }, [sites]);

  const { project, graticule } = useMemo(() => {
    if (!sites.length) return { project: () => ({ x: 0, y: 0 }), graticule: { lons: [], lats: [] } };
    /* A site with no coordinates projects to NaN, and one NaN in a path definition blanks
       the whole SVG. 527 of the centres loaded in the global backfill have no lat/lon yet, so
       they are excluded from the frame rather than allowed to poison it. */
    const onMap = sites.filter(placed);
    if (!onMap.length) return { project: () => ({ x: 0, y: 0 }), graticule: { lons: [], lats: [] } };
    const lons = onMap.map((s) => s.lon), lats = onMap.map((s) => s.lat);
    let minLon = Math.min(...lons), maxLon = Math.max(...lons);
    let minLat = Math.min(...lats), maxLat = Math.max(...lats);
    const padLon = Math.max((maxLon - minLon) * 0.14, 2.2);
    const padLat = Math.max((maxLat - minLat) * 0.14, 1.6);
    minLon -= padLon; maxLon += padLon; minLat -= padLat; maxLat += padLat;

    /* Preserve aspect so geography is not visually distorted. */
    const spanLon = maxLon - minLon, spanLat = maxLat - minLat;
    const scale = Math.min((W - PAD * 2) / spanLon, (H - PAD * 2) / spanLat);
    const ox = (W - spanLon * scale) / 2, oy = (H - spanLat * scale) / 2;

    const project = (lon: number, lat: number) => ({
      x: ox + (lon - minLon) * scale,
      y: oy + (maxLat - lat) * scale,
    });

    const step = spanLon > 40 ? 15 : spanLon > 14 ? 5 : 2;
    const gl: number[] = [], ga: number[] = [];
    for (let l = Math.ceil(minLon / step) * step; l <= maxLon; l += step) gl.push(l);
    for (let l = Math.ceil(minLat / step) * step; l <= maxLat; l += step) ga.push(l);
    return { project, graticule: { lons: gl, lats: ga } };
  }, [sites]);

  const placedCount = sites.filter((s) => Number.isFinite(s.lon) && Number.isFinite(s.lat)).length;
  const maxVol = Math.max(1, ...sites.map((s) => siteTotal(s, organ === 'all' ? undefined : organ)));

  return (
    <div className="card map-card">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className={`map-svg ${drag.current ? 'dragging' : ''}`}
        role="application"
        aria-label={`${TENANT.vocabulary.site} map. Plus and minus zoom, arrow keys pan, 0 fits the view.`}
        tabIndex={0}
        onWheel={onWheel}
        onDoubleClick={(e) => {
          // Double click dives in on the point under the cursor; a drag that ended near its
          // start must not.
          if (dragMoved.current) return;
          const v = toView(e, e.currentTarget);
          zoomAt(1.8, v.x, v.y);
        }}
        onKeyDown={(e) => {
          /* The zoom buttons are real buttons and already tabbable; this makes the map
             surface itself drivable without a pointer. */
          const pan = 48;
          const acts: Record<string, () => void> = {
            '+': () => zoomAt(1.4), '=': () => zoomAt(1.4),
            '-': () => zoomAt(1 / 1.4), '_': () => zoomAt(1 / 1.4),
            ArrowLeft: () => setZ((p) => ({ ...p, x: p.x + pan })),
            ArrowRight: () => setZ((p) => ({ ...p, x: p.x - pan })),
            ArrowUp: () => setZ((p) => ({ ...p, y: p.y + pan })),
            ArrowDown: () => setZ((p) => ({ ...p, y: p.y - pan })),
            '0': () => setZ({ k: 1, x: 0, y: 0 }),
          };
          const act = acts[e.key];
          if (act && e.target === e.currentTarget) { e.preventDefault(); act(); }
        }}
        onPointerDown={(e) => {
          pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
          if (pointers.current.size === 2) {
            /* A second finger turns whatever was happening into a pinch. Capturing both is
               safe here: nobody is waiting for a click from a two-finger gesture. */
            const [a, b] = [...pointers.current.values()];
            pinch.current = { d: Math.hypot(a.x - b.x, a.y - b.y) };
            drag.current = null;
            dragMoved.current = true;
            (e.currentTarget as SVGSVGElement).setPointerCapture?.(e.pointerId);
            return;
          }
          /* Deliberately NOT capturing here. Pointer capture redirects the subsequent click
             to the capturing element, so capturing on pointerdown meant every click landed on
             the <svg> and never on the centre underneath: clicking a dot stopped opening its
             record. Capture is taken below, only once a real drag has started, by which point
             the click is already suppressed anyway. */
          drag.current = { x: e.clientX, y: e.clientY, ox: z.x, oy: z.y };
          dragMoved.current = false;
          /* A tap on empty map lets go of a pinned tooltip. The node's own handler has
             already run by the time this bubbles, so downOnNode says whether the tap was
             on a centre. */
          if (downOnNode.current == null && tipPinned.current) {
            tipPinned.current = false;
            setTip(null);
          }
          downOnNode.current = null;
        }}
        onPointerMove={(e) => {
          if (pointers.current.has(e.pointerId)) {
            pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
          }
          if (pinch.current && pointers.current.size >= 2) {
            const [a, b] = [...pointers.current.values()];
            const d = Math.hypot(a.x - b.x, a.y - b.y);
            if (d > 0 && pinch.current.d > 0) {
              const mid = toView({ clientX: (a.x + b.x) / 2, clientY: (a.y + b.y) / 2 },
                e.currentTarget);
              // Scale by the ratio of finger distances, anchored on the midpoint, which is
              // exactly what fingers on a photograph do.
              zoomAt(d / pinch.current.d, mid.x, mid.y);
            }
            pinch.current = { d };
            return;
          }
          const d = drag.current;
          if (!d) return;
          const dx = e.clientX - d.x, dy = e.clientY - d.y;
          // Three pixels of slop, so a slightly shaky click still opens the centre.
          if (Math.abs(dx) + Math.abs(dy) > 3 && !dragMoved.current) {
            dragMoved.current = true;
            // Now that this is definitely a drag and not a click, capture the pointer so it
            // keeps tracking if it leaves the SVG.
            (e.currentTarget as SVGSVGElement).setPointerCapture?.(e.pointerId);
          }
          const r = e.currentTarget.getBoundingClientRect();
          if (!r.width || !r.height) return;
          const nx = d.ox + dx * (W / r.width);
          const ny = d.oy + dy * (H / r.height);
          /* THE BLANK-MAP BUG. This previously read drag.current!.ox INSIDE the state
             updater. React may flush that updater after the pointer has been released, at
             which point drag.current is null, the non-null assertion throws inside React's
             own render path, and the entire page unmounts to white. The values are now read
             here, while the pointer event is definitely live, and the updater closes over
             plain numbers that cannot become null. */
          setZ((p) => ({ ...p, x: nx, y: ny }));
        }}
        onPointerUp={(e) => {
          pointers.current.delete(e.pointerId);
          if (pointers.current.size < 2) pinch.current = null;
          if ((e.currentTarget as SVGSVGElement).hasPointerCapture?.(e.pointerId)) {
            (e.currentTarget as SVGSVGElement).releasePointerCapture(e.pointerId);
          }
          drag.current = null;
          // Cleared on the next frame rather than immediately: the click event fires after
          // pointerup, and it must still see that a drag happened.
          requestAnimationFrame(() => { dragMoved.current = false; });
        }}
        onPointerCancel={(e) => {
          pointers.current.delete(e.pointerId);
          pinch.current = null;
          drag.current = null;
        }}
      >
        <defs>
          <radialGradient id="glow">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.30" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
          </radialGradient>
        </defs>

        <g transform={`translate(${z.x},${z.y}) scale(${z.k})`}>
        {/* Land first, so graticule and centres draw over it. Stroke width is divided by the
            zoom so borders stay hairlines instead of thickening into slabs at 16x. */}
        {/* Countries we hold data for are tinted and clickable. Without this, Brazil and
            Chad look identical, which quietly implies the map covers everywhere. */}
        <g className="land" strokeWidth={0.6 / z.k}>
          {world.map((c) => {
            const has = mappedNames.has(c.n);
            return c.r.map((ring, i) => (
              <path key={`${c.n}-${i}`}
                    className={has ? 'land-mapped' : undefined}
                    onClick={has ? (e) => { e.stopPropagation(); onPickCountry(c.n); } : undefined}
                    d={'M' + ring.map(([lon, lat]) => {
                      const q = project(lon, lat);
                      return `${q.x.toFixed(1)},${q.y.toFixed(1)}`;
                    }).join('L') + 'Z'}>
                {has && <title>{c.n}, click to filter</title>}
              </path>
            ));
          })}
        </g>
        {graticule.lons.map((l) => {
          const a = project(l, 90), b = project(l, -90);
          return <line key={`x${l}`} x1={a.x} y1={0} x2={b.x} y2={H} className="grat" />;
        })}
        {graticule.lats.map((l) => {
          const a = project(-180, l);
          return <line key={`y${l}`} x1={0} y1={a.y} x2={W} y2={a.y} className="grat" />;
        })}
        {/* Degree labels are world-scale furniture: they sit in projected space, so past 2x
            they scale into letters the size of cities and pan off the edges anyway. */}
        {z.k <= 2 && graticule.lons.map((l) => {
          const a = project(l, 0);
          return <text key={`xl${l}`} x={a.x + 4} y={H - 8} className="grat-label">{Math.abs(l)}°{l < 0 ? 'W' : 'E'}</text>;
        })}
        {z.k <= 2 && graticule.lats.map((l) => {
          const a = project(0, l);
          return <text key={`yl${l}`} x={8} y={a.y - 4} className="grat-label">{Math.abs(l)}°{l < 0 ? 'S' : 'N'}</text>;
        })}

        {sites.filter(placed).map((s) => {
          const p = project(s.lon, s.lat);
          const o = spread.get(s.id);
          const v = siteTotal(s, organ === 'all' ? undefined : organ);
          /* Divided by the zoom factor because this whole layer sits inside a scaled group.
             Without it a dot grows with the map, so zooming in to separate a cluster made the
             dots grow at exactly the rate the gaps did and the blob never resolved. Counter
             scaling holds them at a constant size on screen, which is what lets geography
             spread out underneath them. Labels were already treated this way; the dots were
             simply missed. */
          const r = (5 + Math.sqrt(v / maxVol) * 24) / z.k;
          const isSel = selected?.id === s.id;
          const isLab = MAP_KIND ? s.kinds.includes(MAP_KIND as never) : false;
          return (
            <g
              key={s.id}
              className={`node tier-${s.tier} ${isSel ? 'sel' : ''}`}
              transform={`translate(${p.x + (o?.dx ?? 0)},${p.y + (o?.dy ?? 0)})`}
              onClick={(e) => {
                // A drag across the map should not count as a click on whatever is underneath.
                if (dragMoved.current) return;
                e.stopPropagation();
                /* No hover on glass. The first tap identifies the dot; a second tap on the
                   same dot opens it. A mouse still goes straight through, because the
                   tooltip was already up before the click. */
                if (lastPointerType.current === 'touch'
                    && !(tipPinned.current && tip?.id === s.id)) {
                  tipPinned.current = true;
                  setTip(s);
                  return;
                }
                navigate(`/atlas/site/${s.id}`);
              }}
              onPointerDown={(e) => {
                downOnNode.current = s.id;
                lastPointerType.current = e.pointerType;
              }}
              onPointerEnter={(e) => {
                if (e.pointerType === 'mouse' && !drag.current && !pinch.current) {
                  tipPinned.current = false;
                  setTip(s);
                }
              }}
              onPointerLeave={() => {
                if (!tipPinned.current) setTip((t) => (t && t.id === s.id ? null : t));
              }}
              role="button"
              tabIndex={0}
              aria-label={`${s.name}, ${s.city}`}
              onFocus={() => { tipPinned.current = false; setTip(s); }}
              onBlur={() => { if (!tipPinned.current) setTip(null); }}
              onKeyDown={(e) => e.key === 'Enter' && navigate(`/atlas/site/${s.id}`)}
            >
              {/* The leg back to the true coordinate. Fanned dots sit further from their
                  shared point the deeper the zoom, and without the leg a spread ring reads
                  as centres scattered across a city rather than one address. */}
              {o && z.k >= 2.5 && (
                <line x1={-o.dx} y1={-o.dy} x2={0} y2={0} className="spider-leg" />
              )}
              {s.tier === 1 && s.geo !== 'approx' && z.k < 4 && (
                <circle r={r * 2.1} fill="url(#glow)" className="node-glow" />
              )}
              <circle r={r} className={`node-dot ${s.geo === 'approx' ? 'approx' : ''}`} />
              {isLab && <circle r={r * 0.42} className="node-lab" />}
              <circle r={Math.max(r + 7 / z.k, 11 / z.k)} className="node-hit" />
              {/* Labels are counter-scaled: at 8x zoom an unscaled label is the size of a
                  country. Only shown once there is room, or the map is unreadable.

                  Composed in the transform ATTRIBUTE, about the local origin. The previous
                  scale carried a CSS transform-origin of center, and Chromium resolves that
                  against the view-box, not the dot: every label was flung thousands of pixels
                  from its centre the moment the zoom crossed the threshold, which is why
                  zooming in never seemed to name anything. */}
              {s.geo !== 'approx' && (z.k > 2.5 || sites.length < 90) && (
                <text className="node-label"
                      transform={`translate(0,${-r - 7 / z.k}) scale(${1 / z.k})`}>
                  {s.short}
                </text>
              )}
            </g>
          );
        })}

        {/* How many centres a stacked point is hiding. Until the fan has room to open, three
            institutions on one coordinate render as one dot, and only this count says that
            zooming in will split them. It fades out exactly where the labels come in. */}
        {z.k < 2.5 && clusters.map((c) => {
          const p = project(c.lon, c.lat);
          return (
            <g key={`cl-${c.lat}-${c.lon}`} className="cluster"
               transform={`translate(${p.x},${p.y}) scale(${1 / z.k})`}>
              <text x={8} y={-8} className="cluster-count">{c.n}</text>
            </g>
          );
        })}
        </g>
      </svg>

      {/* The tooltip follows its dot through pan and zoom because its position is computed
          from the same projection and transform, not remembered from the pointer event. */}
      {tip && placed(tip) && (() => {
        const p = project(tip.lon, tip.lat);
        const o = spread.get(tip.id);
        const vx = ((z.x + (p.x + (o?.dx ?? 0)) * z.k) / W) * 100;
        const vy = ((z.y + (p.y + (o?.dy ?? 0)) * z.k) / H) * 100;
        if (vx < 2 || vx > 98 || vy < 4 || vy > 100) return null;
        const v = siteTotal(tip, organ === 'all' ? undefined : organ);
        return (
          <div className="map-tip" style={{ left: `${vx}%`, top: `${vy}%` }} role="status">
            <div className="map-tip-name">{tip.short || tip.name}</div>
            <div className="map-tip-sub">
              {[tip.city, tip.region].filter(Boolean).join(', ')}
            </div>
            <div className="map-tip-stat">
              {fmt(v)}{' '}
              {(organ === 'all' ? TENANT.vocabulary.volumeLabel : `${segLabel(organ)} / yr`).toLowerCase()}
              {' · '}Tier {tip.tier}
            </div>
            <div className="map-tip-hint">
              {lastPointerType.current === 'touch'
                ? 'Tap again to open the record'
                : 'Click to open the record'}
            </div>
          </div>
        );
      })()}

      <div className="map-zoom">
        <button onClick={() => zoomAt(1.4)} aria-label="Zoom in">+</button>
        <button onClick={() => zoomAt(1 / 1.4)} aria-label="Zoom out">−</button>
        {/* The identity transform IS the fitted view: the projection auto-frames whatever
            the filters left, so going home never means an ocean. Named for what it does
            rather than "Reset", which now belongs to the filters. */}
        <button className="mz-reset" onClick={() => setZ({ k: 1, x: 0, y: 0 })}
                aria-label="Fit the whole view"
                disabled={z.k === 1 && z.x === 0 && z.y === 0}>Fit view</button>
      </div>

      {/* 527 centres arrived in the global backfill without coordinates. They are real and
          they are in the Atlas; they simply cannot be drawn yet, and a map that silently
          omits a third of its own dataset is misleading. */}
      {sites.length > placedCount && (
        <div className="map-unplaced">
          {fmt(sites.length - placedCount)} of {fmt(sites.length)} centres have no coordinates
          yet and are not shown. They appear in the {TENANT.vocabulary.sitePlural.toLowerCase()} table.
        </div>
      )}

      <div className="map-legend">
        <div className="legend-row">
          <span className="label">Tier</span>
          <span className="lg lg-1" /> 1
          <span className="lg lg-2" /> 2
          <span className="lg lg-3" /> 3
        </div>
        {/* The ringed marker means whatever the tenant's first flagged site kind means. It
            said "has H&I laboratory" to every customer, including those with no laboratories. */}
        {MAP_KIND && (
          <div className="legend-row">
            <span className="label">Marker</span>
            <span className="lg-ring" /> {
              TENANT.vocabulary.siteKinds.find((k) => k.key === MAP_KIND)?.label
            }
          </div>
        )}
        <div className="legend-row legend-note">Dot area is proportional to annual volume.</div>
      </div>
    </div>
  );
}

/* =====================================================================================
   Centres table
   ===================================================================================== */


function CentresTable({ sites, organ, onSelect, scoreById }: {
  sites: Site[]; organ: SegmentKey | 'all'; onSelect: (s: Site) => void;
  /* Present only when a PREFER criterion is set. Without it the order is the pickers' own and a
     Fit column would be a column of identical numbers pretending to mean something. */
  scoreById?: Map<string, number> | null;
}) {
  const max = Math.max(1, ...sites.map((s) => siteTotal(s, organ === 'all' ? undefined : organ)));
  return (
    <div className="card table-wrap">
      <table className="data">
        <thead>
          <tr>
            {/* The tenant's word for a site. This column was headed "Centre" for every
                customer, including the one whose own vocabulary says "Treatment centre". */}
            {scoreById && <th className="num">Fit</th>}
            <th>{TENANT.vocabulary.site}</th>
            <th>Location</th>
            <th>Tier</th>
            <th className="num">{organ === 'all' ? TENANT.vocabulary.volumeLabel : `${segLabel(organ)} / yr`}</th>
            {HAS_PREVALENCE && <th className="num">{TENANT.vocabulary.population}</th>}
            {HAS_WAITING && <th className="num">Waiting list</th>}
            {KIND_COLUMNS.map((k) => <th key={k.key}>{k.label}</th>)}
            <th>Incumbent</th>
          </tr>
        </thead>
        <tbody>
          {sites.map((s) => {
            const v = siteTotal(s, organ === 'all' ? undefined : organ);
            return (
              <tr key={s.id} onClick={() => onSelect(s)} style={{ cursor: 'pointer' }}>
                {scoreById && (
                  <td className="num">
                    <span className="sb-score"
                          style={{ opacity: 0.35 + ((scoreById.get(s.id) ?? 0) / 100) * 0.65 }}>
                      {(scoreById.get(s.id) ?? 0).toFixed(0)}
                    </span>
                  </td>
                )}
                <td>
                  <div className="cell-title">{s.short}</div>
                  <div className="cell-sub">{s.name}</div>
                </td>
                <td>
                  <div className="cell-title">{s.city}</div>
                  <div className="cell-sub">{s.region}</div>
                </td>
                <td><TierPill tier={s.tier} /></td>
                <td className="num">
                  <div className="vol-cell">
                    <span>{fmt(v)}</span>
                    <div className="bar"><span style={{ width: `${(v / max) * 100}%` }} /></div>
                  </div>
                </td>
                {HAS_PREVALENCE && <td className="num">
                  {s.prevalentGrafts
                    ? <span title={s.prevalentGrafts.method}>{fmt(s.prevalentGrafts.value)}</span>
                    : <span className="muted">—</span>}
                </td>}
                {HAS_WAITING && <td className="num">{s.waitingList != null ? fmt(s.waitingList) : <span className="muted">—</span>}</td>}
                {KIND_COLUMNS.map((k) => (
                  <td key={k.key}>{s.kinds.includes(k.key as never)
                    ? <span className={`pill ${k.pill}`}>{k.label}</span>
                    : <span className="muted">—</span>}</td>
                ))}
                <td>
                  {s.incumbent?.length
                    ? s.incumbent.map((i) => <span key={i} className="pill warn" style={{ marginRight: 4 }}>{i}</span>)
                    : <span className="muted">unknown</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* =====================================================================================
   Reimbursement board. Grouped by country, because the route is a property of the
   jurisdiction rather than of the product.
   ===================================================================================== */

function ReimbursementBoard({ country, market }: { country: string; market: string | null }) {
  /* A route with no market code applies to every market: a payer's procedure belongs to the
     jurisdiction. One with a code is specific, and is hidden when a different market is
     selected. With no market selected, everything shows. */
  const rows = ALL_PATHWAYS.filter((p) => (country === 'all' || p.iso3 === country)
    && (!market || !p.market || p.market === market));
  const byCountry = rows.reduce<Record<string, Pathway[]>>((a, p) => {
    (a[p.iso3] ||= []).push(p);
    return a;
  }, {});

  return (
    <div className="reimb">
      {Object.entries(byCountry).map(([iso3, list]) => {
        const c = COUNTRIES.find((x) => x.iso3 === iso3);
        return (
          <section key={iso3} className="reimb-country">
            <h2 className="reimb-country-title">
              {c?.name ?? iso3}
              <span className="pill mute">{list.length} route{list.length === 1 ? '' : 's'}</span>
            </h2>
            <div className="reimb-grid">
              {list.map((p) => (
                <article key={p.id} className={`card reimb-card diff-${p.difficulty}`}>
                  <header className="reimb-head">
                    <div>
                      <h3>{p.routeName}</h3>
                      <div className="reimb-body-name">{p.decisionBody}</div>
                    </div>
                    <span className={`pill ${p.difficulty === 'low' ? 'ok' : p.difficulty === 'medium' ? 'info' : p.difficulty === 'high' ? 'warn' : 'risk'}`}>
                      {p.difficulty.replace('_', ' ')}
                    </span>
                  </header>

                  <p className="reimb-how">{p.howItWorks}</p>

                  {p.opportunity && (
                    <div className="reimb-opp">
                      <span className="label">Commercial read</span>
                      <p>{p.opportunity}</p>
                    </div>
                  )}

                  <footer className="reimb-foot">
                    <Conf c={p.confidence} />
                    {p.submissionWindow && <span className="reimb-window">{p.submissionWindow}</span>}
                    {p.priceLow && (
                      <span className="reimb-price">
                        {p.currency} {fmt(p.priceLow)}–{fmt(p.priceHigh)}
                      </span>
                    )}
                    {p.nextDeadline && <span className="pill risk">Due {p.nextDeadline}</span>}
                  </footer>
                </article>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/* =====================================================================================
   Site detail
   ===================================================================================== */

function SiteDrawer({ site, onClose }: { site: Site; onClose: () => void }) {
  const c = COUNTRIES.find((x) => x.iso3 === site.iso3);
  const navigate = useNavigate();
  const [capturing, setCapturing] = useState(false);
  // These two were rendered as buttons with no handler at all, so the panel offered actions
  // that did nothing. Same forms the full record uses, so there is one implementation.
  const [creatingOpp, setCreatingOpp] = useState(false);
  const [logging, setLogging] = useState(false);
  const intel = useList<any>('intelligence').filter((i) => i.objectId === site.id);
  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <aside className="drawer card" role="dialog" aria-label={site.name}>
        <header className="drawer-head">
          <div>
            <div className="drawer-eyebrow">{site.city} · {site.region} · {c?.name}</div>
            <h2 className="drawer-title display">{site.name}</h2>
            <div className="drawer-tags">
              <TierPill tier={site.tier} />
              {TENANT.vocabulary.siteKinds.filter((k) => site.kinds.includes(k.key as never))
                .map((k) => <span key={k.key} className={`pill ${k.pill}`}>{k.label}</span>)}
              {site.paediatric && <span className="pill info">Paediatric</span>}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>

        <div className="drawer-body">
          <section>
            <div className="label">Annual volume, {site.volumes[0]?.year}</div>
            <table className="data compact">
              <tbody>
                {site.volumes.map((v) => (
                  <tr key={v.organ}>
                    <td>{segLabel(v.organ)}</td>
                    <td className="num"><strong>{fmt(v.total)}</strong></td>
                    <td className="num muted">{v.living != null ? `${v.living} living` : ''}</td>
                    <td><Conf c={v.confidence} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {site.prevalentGrafts && (
            <section>
              <div className="label">Prevalent followed grafts</div>
              <div className="big-number">{fmt(site.prevalentGrafts.value)}</div>
              <div className="drawer-note">
                <Conf c={site.prevalentGrafts.confidence} /> {site.prevalentGrafts.method}.
                This, not incident volume, is the addressable base for a surveillance assay.
              </div>
            </section>
          )}

          {(site.waitingList || site.dcdListed || site.dualKidney) && (
            <section>
              <div className="label">Waiting list composition</div>
              <div className="kv-grid">
                {site.waitingList != null && <KV k="Active on list" v={fmt(site.waitingList)} />}
                {site.dcdListed != null && <KV k="DCD listed" v={fmt(site.dcdListed)} />}
                {site.dualKidney != null && <KV k="Dual kidney" v={fmt(site.dualKidney)} />}
              </div>
            </section>
          )}

          {site.labNote && (
            <section>
              <div className="label">Laboratory</div>
              <p className="drawer-text">{site.labNote}</p>
            </section>
          )}

          {site.note && (
            <section>
              <div className="label">Commercial assessment</div>
              <p className="drawer-text">{site.note}</p>
            </section>
          )}

          {intel.length > 0 && (
            <section>
              <div className="label">Field intelligence ({intel.length})</div>
              <div className="intel-list">
                {intel.map((i) => (
                  <div key={i.id} className="intel">
                    <div className="intel-head">
                      <span className={`pill ${i.reliability === 'high' ? 'ok' : i.reliability === 'unconfirmed' ? 'risk' : 'mute'}`}>
                        {i.reliability}
                      </span>
                      <span className="pill mute">{String(i.category).replace(/_/g, ' ')}</span>
                      {i.capturedOffline && <span className="pill warn">captured offline</span>}
                    </div>
                    <p className="intel-headline">{i.headline}</p>
                    {i.detail && <p className="drawer-text">{i.detail}</p>}
                    <div className="intel-foot">
                      {String(i.sourceType).replace(/_/g, ' ')} · {i.sourceDetail} · observed {i.observedOn} · expires {i.expiresOn}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section>
            <div className="label">Actions</div>
            <div className="drawer-actions">
              <button className="btn primary" onClick={() => setCreatingOpp(true)}>Create opportunity</button>
              <button className="btn" onClick={() => setLogging(true)}>Log interaction</button>
              <button className="btn" onClick={() => setCapturing(true)}>Add intelligence</button>
              <button className="btn primary" onClick={() => navigate(`/atlas/site/${site.id}`)}>
                Open full record
              </button>
            </div>
          </section>
        </div>
      </aside>
      {creatingOpp && (
        <NewOpportunity siteId={site.id} siteName={site.short || site.name}
                        onClose={() => setCreatingOpp(false)}
                        onDone={() => setCreatingOpp(false)} />
      )}
      {logging && (
        <LogInteraction siteId={site.id} siteName={site.short || site.name}
                        onClose={() => setLogging(false)}
                        onDone={() => setLogging(false)} />
      )}
      {capturing && (
        <CaptureIntelligence
          objectType="site" objectId={site.id} objectName={site.name}
          onClose={() => setCapturing(false)}
        />
      )}
    </>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div className="kv">
      <div className="kv-k">{k}</div>
      <div className="kv-v">{v}</div>
    </div>
  );
}
